import React from 'react';
import { useTranslation } from 'react-i18next';
import './_styles_/BidsAuctionConfiguration.css';
import {
    auctionPropertiesType,
    auctionType as auctionTypeRef,
} from '../../types';
import {
    ENERGY,
    getProductType,
    SECURITY,
} from '../../utils/ProductTypes';
import { hasPrivilege } from '../../hocs/withPrivileges';
import useAuthentication from '../../hooks/useAuthentication';
import Privileges from '../../utils/Privileges';
import ReadonlyField from '../fields/ReadonlyField';
import ReadonlyNumberField from '../fields/ReadonlyNumberField';
import {
    getBiddingType,
    OPEN,
    SLOTTED,
} from '../../utils/BiddingTypes';
import {
    BID_PRICE,
    getTradePriceCalculationType,
} from '../../utils/TradePriceCalculationTypes';
import { getSettlementStartType } from '../../utils/SettlementStartTypes';
import { getAuctionAlgorithm } from '../../utils/AuctionAlgorithms';
import {
    BUY,
    getAuctionType,
    SELL,
} from '../../utils/AuctionTypes';
import {
    AUTO,
    getAuctionAcceptanceType,
} from '../../utils/AuctionAcceptanceTypes';
import Collapsible from '../Collapsible';

BidsAuctionConfiguration.propTypes = {
    auction: auctionTypeRef.isRequired,
    auctionProps: auctionPropertiesType.isRequired,
};

function BidsAuctionConfiguration(props) { // NOSONAR javascript:S3776
    const {
        auction,
        auctionProps,
    } = props;
    const {
        availableAuctionTypes,
        availableBiddingTypes,
        availableTradePriceCalculationTypes,
        availableAuctionAlgorithms,
        availableAuctionAcceptanceTypes,
    } = auctionProps;
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const currencyCode = auction.zone.currency.code;
    const currencyPostfix = <>&nbsp;{currencyCode}</>;
    const productType = getProductType(auction.product.productType);
    const auctionType = getAuctionType(auction.auctionType);
    const auctionAcceptanceType = getAuctionAcceptanceType(auction.auctionAcceptanceType);
    const tradePriceCalculationType = getTradePriceCalculationType(auction.tradePriceCalculationType);
    const biddingType = getBiddingType(auction.biddingType);
    const userHasReadAllPrivilege = hasPrivilege(userPrivileges, [Privileges.AUCTION_DETAIL_VIEW_ALL]);
    const userHasReadPartsPrivilege = hasPrivilege(userPrivileges, [Privileges.AUCTION_DETAIL_VIEW_ALL, Privileges.AUCTION_VIEW_ALL, Privileges.AUCTION_VIEW_OWN]);
    const userHasReadMinMaxPricePrivilege = hasPrivilege(userPrivileges, [Privileges.AUCTION_DETAIL_VIEW_ALL, Privileges.AUCTION_VIEW_ALL]);
    const canSeeMinPrice = userHasReadAllPrivilege || (userHasReadMinMaxPricePrivilege && auction.minPrice !== null);
    const canSeeMaxPrice = userHasReadAllPrivilege || (userHasReadMinMaxPricePrivilege && auction.maxPrice !== null);
    const isVisible = userHasReadAllPrivilege
        || (userHasReadPartsPrivilege
            && (
                productType.is(SECURITY)
                || biddingType.is(OPEN)
                || availableAuctionTypes.length > 1
                || availableBiddingTypes.length > 1
                || availableTradePriceCalculationTypes.length > 1
                || availableAuctionAlgorithms.length > 1
                || availableAuctionAcceptanceTypes.length > 1
            )
        );
    return (
        isVisible && (
            <Collapsible
                header={<h5>{t('auctions.auction-setup.auction-configuration')}</h5>}
                className="bids-popup--inner-container"
            >
                <div className="auction-bids--auction-configuration">
                    {productType.is(ENERGY) && userHasReadAllPrivilege && (
                        <ReadonlyNumberField
                            label={t('auctions.auction-setup.number-of-lots')}
                            value={auction.quantity}
                            className="auction-bids--auction-configuration--fields--number-of-lots"
                        />
                    )}
                    {(auctionType.is(BUY) || (auctionType.is(SELL) && auctionAcceptanceType.is(AUTO))) && userHasReadAllPrivilege && (
                        <ReadonlyNumberField
                            label={t('auctions.auction-setup.lowest-acceptable-price')}
                            value={auction.lowestAcceptablePrice}
                            fractionDigits={2}
                            className="auction-bids--auction-configuration--fields--lowest-acceptable-price"
                            postfix={currencyPostfix}
                        />
                    )}
                    {((auctionType.is(BUY) && auctionAcceptanceType.is(AUTO)) || auctionType.is(SELL)) && userHasReadAllPrivilege && (
                        <ReadonlyNumberField
                            label={t('auctions.auction-setup.highest-acceptable-price')}
                            value={auction.highestAcceptablePrice}
                            fractionDigits={2}
                            className="auction-bids--auction-configuration--fields--highest-acceptable-price"
                            postfix={currencyPostfix}
                        />
                    )}
                    {availableAuctionTypes.length > 1 && userHasReadPartsPrivilege && (
                        <ReadonlyField
                            label={t('auctions.auction-setup.auction-type')}
                            value={auctionType.translate(t)}
                            className="auction-bids--auction-configuration--fields--auction-type"
                        />
                    )}
                    {availableBiddingTypes.length > 1 && userHasReadPartsPrivilege && (
                        <ReadonlyField
                            label={t('auctions.auction-setup.bidding-type')}
                            value={biddingType.translate(t)}
                            className="auction-bids--auction-configuration--fields--bidding-type"
                        />
                    )}
                    {availableTradePriceCalculationTypes.length > 1 && userHasReadPartsPrivilege && (
                        <ReadonlyField
                            label={t('auctions.auction-setup.trade-price-calculation-type')}
                            value={tradePriceCalculationType.translate(t)}
                            className="auction-bids--auction-configuration--fields--trade-price-calculation-type"
                        />
                    )}
                    {availableAuctionAlgorithms.length > 1 && userHasReadPartsPrivilege && (
                        <ReadonlyField
                            label={t('auctions.auction-setup.algorithm')}
                            value={getAuctionAlgorithm(auction.algorithm).translate(t)}
                            className="auction-bids--auction-configuration--fields--algorithm"
                        />
                    )}
                    {availableAuctionAcceptanceTypes.length > 1 && userHasReadPartsPrivilege && (
                        <ReadonlyField
                            label={t('auctions.auction-setup.acceptance-type')}
                            value={getAuctionAcceptanceType(auction.auctionAcceptanceType).translate(t)}
                            className="auction-bids--auction-configuration--fields--acceptance-type"
                        />
                    )}
                    {tradePriceCalculationType.is(BID_PRICE) && userHasReadAllPrivilege && (
                        <ReadonlyField
                            label={t('auctions.auction-setup.show-last-price')}
                            value={auction.showLastPrice ? t('misc.yes') : t('misc.no')}
                            className="auction-bids--auction-configuration--fields--show-last-price"
                        />
                    )}
                    {biddingType.is(SLOTTED) && userHasReadAllPrivilege && (
                        <>
                            <ReadonlyField
                                label={t('auctions.auction-setup.show-ranking')}
                                value={auction.showRanking ? t('misc.yes') : t('misc.no')}
                                className="auction-bids--auction-configuration--fields--show-ranking"
                            />
                            <ReadonlyField
                                label={t('auctions.auction-setup.limit-bid-modification')}
                                value={auction.limitBidModification ? t('misc.yes') : t('misc.no')}
                                className="auction-bids--auction-configuration--fields--limit-bid-modification"
                            />
                        </>
                    )}
                    {canSeeMinPrice && (
                        <ReadonlyNumberField
                            label={t('auctions.auction-setup.min-price')}
                            value={auction.minPrice}
                            emptyValue={t('auctions.auction-setup.no-min-price')}
                            fractionDigits={2}
                            postfix={currencyPostfix}
                            className="auction-bids--auction-configuration--fields--min-price"
                        />
                    )}
                    {canSeeMaxPrice && (
                        <ReadonlyNumberField
                            label={t('auctions.auction-setup.max-price')}
                            value={auction.maxPrice}
                            emptyValue={t('auctions.auction-setup.no-max-price')}
                            fractionDigits={2}
                            postfix={currencyPostfix}
                            className="auction-bids--auction-configuration--fields--max-price"
                        />
                    )}
                    {userHasReadAllPrivilege && (
                        <>
                            <ReadonlyNumberField
                                label={t('auctions.auction-setup.spot-reference-price')}
                                value={auction.spotReferencePrice}
                                emptyValue={t('auctions.auction-setup.no-spot-reference-price')}
                                fractionDigits={2}
                                postfix={currencyPostfix}
                                className="auction-bids--auction-configuration--fields--spot-reference-price"
                            />
                            <ReadonlyNumberField
                                label={t('auctions.auction-setup.abs-deviation-limit')}
                                value={auction.absoluteDeviation}
                                emptyValue={t('auctions.auction-setup.no-abs-deviation-limit')}
                                fractionDigits={2}
                                postfix={currencyPostfix}
                                className="auction-bids--auction-configuration--fields--absolute-deviation"
                            />
                            <ReadonlyNumberField
                                label={t('auctions.auction-setup.rel-deviation-limit')}
                                value={auction.relativeDeviation}
                                emptyValue={t('auctions.auction-setup.no-rel-deviation-limit')}
                                fractionDigits={2}
                                postfix={<>&nbsp;%</>}
                                className="auction-bids--auction-configuration--fields--relative-deviation"
                            />
                            <ReadonlyField
                                label={t('auctions.auction-setup.description')}
                                value={auction.description}
                                emptyValue={t('auctions.auction-setup.no-description')}
                                className="auction-bids--auction-configuration--fields--description"
                            />
                        </>
                    )}
                    {productType.is(SECURITY) && userHasReadAllPrivilege && (
                        <ReadonlyField
                            label={t('auctions.auction-setup.fast-market')}
                            value={auction.fastMarket ? t('misc.yes') : t('misc.no')}
                            className="auction-bids--auction-configuration--fields--fast-market"
                        />
                    )}
                    {productType.is(SECURITY) && userHasReadPartsPrivilege && (
                        <ReadonlyField
                            label={t('auctions.auction-setup.settlement-start')}
                            value={getSettlementStartType(auction.settlStartType).translate(t)}
                            className="auction-bids--auction-configuration--fields--settlement-start"
                        />
                    )}
                    {biddingType.is(OPEN) && userHasReadPartsPrivilege && (
                        <>
                            <ReadonlyNumberField
                                label={t('auctions.auction-setup.smallest-unit')}
                                value={auction.smallestUnit}
                                className="auction-bids--auction-configuration--fields--smallest-unit"
                            />
                            <ReadonlyNumberField
                                label={t('auctions.auction-setup.maximum-bid-size')}
                                value={auction.maxBidSize}
                                emptyValue={t('auctions.auction-setup.no-limit')}
                                className="auction-bids--auction-configuration--fields--maximum-bid-size"
                            />
                        </>
                    )}
                </div>
            </Collapsible>
        )
    );
}

export default BidsAuctionConfiguration;
